import { CUSTOMER_LEVEL, TYRE_IDENTIFIER_FIELD } from '@wyz/types';
import {
  MAX_LENGTH_MESSAGE,
  REQUIRED_MESSAGE,
  requiredEmail,
  requiredString,
} from '@wyz/utils';
import { z } from 'zod';
import { ENTITIES_STATUSES } from '~/common/schemas';
// eslint-disable-next-line @wyz/module/no-relative-import
import { vehicleTypeGroupSchema } from './setting-templates';

export const CustomerQuoteDiagnosisProvider = {
  TYRE_STATION: 'TYRE_STATION',
  CAROOL: 'CAROOL',
  T_SCAN: 'T-SCAN',
} as const;

export const CustomerQuoteTarget = {
  SMBB: 'SMBB',
  FLEET_BACK: 'FLEET_BACK',
} as const;

// Form starts
const deliveryInformationSchema = z.object({
  address: requiredString,
  additionalAddress: z.string().nullish(),
  zipCode: requiredString,
  city: requiredString,
  country: z
    .object({
      id: requiredString,
      isoCode: z.string(),
    })
    .nullish(),
});

const billingInformationSchema = z.object({
  ecotaxZone: z.coerce.number<number>({
    error: REQUIRED_MESSAGE,
  }),
  vatZone: z.number().nullish(), // not required for N2
  billingCode: z.string().nullish(),
  _level: z.number().nullish(),
});
const configurationSchema = z.object({
  buyerId: z.string().nullish(),
  orderingId: z.string().nullish(),
  consigneeId: z.string().nullish(),
  dmsRef: z.string().nullish(),
  eligibleToContainerOrders: z.boolean().default(false).optional(),
});

const logoScheme = z
  .object({
    id: z.string(),
    resourcesFormat: z.array(z.object({ url: z.string() })),
  })
  .nullish();

const quoteInformationSchema = z.object({
  diagnosisProvider: z.enum(CustomerQuoteDiagnosisProvider).nullish(),
  express: z.union([z.literal(0), z.literal(1)]),
  target: z.enum(CustomerQuoteTarget).nullish(),
});

const formSchema = z.object({
  id: z.string().nullish(),
  customerGroup: z
    .object({
      label: z.string().nullish(),
      value: z.string().nullish(),
    })
    .nullish(),
  // Note: on the server side, we keep the property name as 'code' for, but on the frontend, we want to rename it to 'customerCode' to avoid probems with some chromel extensions that automatically clean up fields named 'code'
  customerCode: z
    .string({
      error: (issue) =>
        issue.input === undefined ? REQUIRED_MESSAGE : REQUIRED_MESSAGE,
    })
    .trim()
    .min(1, {
      message: REQUIRED_MESSAGE,
    })
    .max(20, { message: MAX_LENGTH_MESSAGE }),
  name: requiredString,
  commercialName: z.string({}).nullish(),
  legalForm: z.string().nullish(),
  rcsNumber: z.string().nullish(),
  vatNumber: z.string().nullish(),
  _requiredParent: z.boolean().nullish(),
  _requiredCustomerGroup: z.boolean().nullish(),
  parent: z
    .object({
      id: z
        .object({
          label: z.string(),
          value: z.string(),
          code: z.string(),
        })
        .nullish(),
    })
    .nullish(),
  logo: logoScheme,
  customName: z.string().nullish(),
  locale: requiredString,
  isSupplier: z.boolean({
    error: REQUIRED_MESSAGE,
  }),
  deliveryInformation: deliveryInformationSchema,
  billingInformation: billingInformationSchema.superRefine((formData, ctx) => {
    // Conditional validation
    if (formData._level === CUSTOMER_LEVEL.N1 && !formData.billingCode) {
      ctx.addIssue({
        code: 'custom',
        message: REQUIRED_MESSAGE,
        path: ['billingCode'],
        fatal: true,
      });
    }
    if (formData._level === CUSTOMER_LEVEL.N1 && !formData.vatZone) {
      ctx.addIssue({
        code: 'custom',
        message: REQUIRED_MESSAGE,
        path: ['vatZone'],
        fatal: true,
      });
    }
  }),
  configuration: configurationSchema,
  criteria: z.array(
    z.object({
      criterion: z.object({ id: z.string() }),
      value: requiredString,
    }),
  ),
  quoteInformation: quoteInformationSchema,
  redistributionMaxDelay: z.number().min(1).nullish(),
});

export const suppliersFormSchema = z.object({
  supplierConfigurations: z.array(
    z.object({
      wyzApiId: z.number(),
      buyerId: z.string().nullish(),
      consigneeId: z.string().nullish(),
      orderingId: z.string().nullish(),
    }),
  ),
});
const suppliersTemplateSchema = z.object({
  supplierConfigurations: z.array(
    z.object({
      wyzApiId: z.number(),
      buyerId: z.string().nullish(),
      consigneeId: z.string().nullish(),
      orderingId: z.string().nullish(),
    }),
  ),
});
export const redistributionBrandsFormSchema = z.object({});
export const quoteBrandsFormSchema = z.object({});
export const brandsFormSchema = z.object({});
export const customerSettingTemplateFormSchema = z.object({});
export const internalStocksFormSchema = z.object({
  internalStocks: z.array(
    z.object({
      name: z.string(),
      id: z.number(),
    }),
  ),
});

export const contactFormSchema = z.object({
  contacts: z.array(
    z
      .object({
        email: requiredEmail,
        phone: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        types: z.array(z.string()),
        default: z.boolean(),
      })
      .required(),
  ),
});

export const configurationFormSchema = formSchema.pick({ configuration: true });
export const entityCriteriaFormSchema = formSchema.pick({ criteria: true });
export const generalFormSchema = formSchema.pick({
  customerGroup: true,
  customerCode: true,
  name: true,
  commercialName: true,
  legalForm: true,
  rcsNumber: true,
  vatNumber: true,
  parent: true,
  _requiredParent: true,
  _requiredCustomerGroup: true,
  customName: true,
  logo: true,
  redistributionMaxDelay: true,
  locale: true,
});
export const generalFormValidationSchema = generalFormSchema.superRefine(
  (formData, ctx) => {
    // Conditional validation
    if (formData._requiredParent && !formData.parent?.id) {
      ctx.addIssue({
        code: 'custom',
        message: REQUIRED_MESSAGE,
        path: ['parent', 'id'],
        fatal: true,
      });
    }
    if (formData._requiredCustomerGroup && !formData.customerGroup?.value) {
      ctx.addIssue({
        code: 'custom',
        message: REQUIRED_MESSAGE,
        path: ['customerGroup.value'],
        fatal: true,
      });
    }
  },
);
export const deliveryAndBillingFormSchema = formSchema.pick({
  deliveryInformation: true,
  billingInformation: true,
});

export const quoteInformationFormSchema = formSchema.pick({
  quoteInformation: true,
});

// Form Ends
export const minimalCustomerSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  city: z.string(),
  commercialName: z.string(),
  level: z.number(),
});

const entityCriteriaSchema = z.object({
  criteria: z.array(
    z.object({
      criterion: z.object({ id: z.string(), name: z.string().nullish() }),
      value: z.object({
        id: z.string(),
        label: z.string(),
      }),
    }),
  ),
});

const baseBrandTemplate = z
  .object({
    brands: z.array(
      z.object({
        externalId: z.object({
          id: z.string(),
        }),
      }),
    ),
  })
  .nullish();
export const userCustomerSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  level: z.number(),
  contacts: z.array(
    z.object({
      email: z.string(),
      phone: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      types: z.array(z.string()),
    }),
  ),
  isSupplier: z.boolean(),
  hasProximityStocks: z.boolean(),
  hasInternalStocks: z.boolean(),
  deliveryInformation: z.object({
    city: z.string(),
    zipCode: z.string(),
    timeZone: z.string(),
    country: z.object({
      id: z.string(),
      currencyId: z.number().nullish(),
      currencyIsoCode: z.string().optional(),
      chatId: z.string().optional(),
    }),
    customerRelationshipContact: z
      .object({
        email: z.string().nullish(),
        phone: z.string().nullish(),
      })
      .nullish(),
  }),
  // this is the resolvedQuoteBrandTemplate (the fallback is already handled on the server)
  quoteBrandTemplate: baseBrandTemplate.nullish(),
  settingTemplate: z
    .object({
      generalSetting: z.object({
        referenceGroupPublicKey: z.string().nullish(),
        tyreIdentifierFields: z.array(z.enum(TYRE_IDENTIFIER_FIELD)).nullish(),
        displayBestPriceTag: z.boolean().nullish(),
        displayBestDelayTag: z.boolean().nullish(),
        defaultSeasons: z.array(z.string()),
        defaultWidthMin: z.number().nullish(),
        defaultWidthMax: z.number().nullish(),
        destockingEnabled: z.boolean().nullish(),
        manualSearch: z.boolean(),
      }),
      vehicleTypeGroups: z.array(
        vehicleTypeGroupSchema.extend({ vehicleTypeIds: z.array(z.string()) }),
      ),
      alternativeOfferSetting: z.object({
        j0: z.boolean().nullable(),
      }),
    })
    .nullish(),
  purchaseSearchable: z.boolean(),
  wcwSearchable: z.boolean(),
  quoteSearchable: z.boolean(),
  expressQuoteSearchable: z.boolean(),
  quoteInformation: z.object({
    express: z.boolean(),
  }),
});

const proximityStockSchema = z.object({
  proximityStocks: z.array(
    z.object({
      rank: z.number(),
      alias: z.string(),
      customer: z.object({
        code: z.string(),
        name: z.string(),
        deliveryInformation: z.object({
          city: z.string(),
        }),
      }),
    }),
  ),
});
const internalStockSchema = z.object({
  internalStocks: z.array(
    z.object({
      rank: z.number(),
      alias: z.string().nullish(),
      supplierId: z.number(),
    }),
  ),
});

export const entitySchema = formSchema
  .pick({
    name: true,
    commercialName: true,
    customName: true,
    legalForm: true,
    rcsNumber: true,
    vatNumber: true,
    isSupplier: true,
    quoteInformation: true,
    redistributionMaxDelay: true,
    locale: true,
  })
  .extend({
    code: z.string(),
    id: z.string(),
    root: z.boolean(),
    level: z.number(),
    status: z.enum(ENTITIES_STATUSES),
    parent: minimalCustomerSchema,
    customerGroup: z
      .object({
        id: z.number(),
        name: z.string(),
      })
      .nullable(),
    // Virtual Property
    parentsIds: z.array(z.string()),
    hasChildren: z.boolean(),
    usersCount: z.number(),
    logisticContact: z
      .object({
        firstName: z.string().nullish(),
        lastName: z.string().nullish(),
        phone: z.string().nullish(),
        email: z.string().nullish(),
      })
      .nullish(),
  })
  .extend(proximityStockSchema.shape)
  .extend(internalStockSchema.shape)
  .extend(entityCriteriaSchema.shape)
  .extend(suppliersTemplateSchema.shape)
  .extend(contactFormSchema.shape)
  .extend(deliveryAndBillingFormSchema.shape)
  .extend(configurationFormSchema.shape);

export const entityCreatePayload = generalFormSchema
  .omit({ customerCode: true }) // we will handle the renaming of 'customerCode' to 'code' in the helper function formToPayload
  .extend(suppliersFormSchema.shape)
  .extend(internalStocksFormSchema.shape)
  .extend(contactFormSchema.shape)
  .extend(deliveryAndBillingFormSchema.shape)
  .extend(configurationFormSchema.shape)
  .extend(entityCriteriaSchema.shape)
  .extend(internalStockSchema.shape)
  .extend({
    status: z.enum(ENTITIES_STATUSES),
    customerGroup: z
      .object({
        id: z.number(),
        name: z.string(),
      })
      .nullish(),
    parent: z.object({ id: z.string() }).nullish(),
    logo: z.object({ id: z.string() }).nullish(),
    code: z.string(),
  });
